import { useEffect, useState } from "react";

import { Button, Classes, Colors, Icon, Position } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { formatDistanceToNow } from "date-fns";
import upperFirst from "lodash/upperFirst";

import CalendarIcon from "@/../../hypetrain/static/img/calendar_black.svg";
import CalendarGrayCheckIcon from "@/../../hypetrain/static/img/calendar-check-gray.svg";
import CalendarGrayIcon from "@/../../hypetrain/static/img/calendar-gray.svg";
import GoogleCalendarIcon from "@/../../hypetrain/static/img/google_calendar.svg";
import OutlookIcon from "@/../../hypetrain/static/img/outlook-icon.svg";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { BP_NS, BRAND_PURPLE, CARD_CLASSES } from "@/css/constants";
import { useProgramComponentParticipantsQueryLazyQuery } from "@/graphql";
import {
  LeadComponent,
  MainComponent,
  MainProgram,
  ParticipantComponent,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { getBrowserTimezone } from "@/utils/datetime";

import { getCloseDate } from "./utils";
import {
  type CalendarType,
  generateAllCalendarData,
} from "./utils/calendar-utils";

const useCalendarPreference = (): [
  CalendarType,
  (value: CalendarType) => void,
] => {
  const [preferredCalendar, setPreferredCalendarState] =
    useState<CalendarType | null>(
      localStorage.getItem("preferredCalendar") as CalendarType | null,
    );

  useEffect(() => {
    // Listen for storage changes from other components
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "preferredCalendar") {
        setPreferredCalendarState(e.newValue as CalendarType);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const setPreferredCalendar = (value: CalendarType) => {
    localStorage.setItem("preferredCalendar", value);
    setPreferredCalendarState(value);

    // Dispatch an event to notify other components
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: "preferredCalendar",
        newValue: value,
      }),
    );
  };

  return [preferredCalendar ?? "google", setPreferredCalendar];
};

function useCalendarClick(
  component: LeadComponent | ParticipantComponent | MainComponent,
  program: MainProgram,
): {
  handleCalendarSelect: (service: CalendarType) => void;
  clickHistory: Record<
    string,
    { lastClicked: number; calendarType: CalendarType }
  >;
  preferredCalendar: CalendarType;
  wasClicked: boolean;
  loading: boolean;
} {
  const [fetch, { loading }] = useProgramComponentParticipantsQueryLazyQuery();

  const [selectedTimezone] = useState(getBrowserTimezone());

  // Convert all dates to the selected timezone
  const closeDate = getCloseDate(component);
  const parsedEndDate = closeDate ? new Date(closeDate) : null;
  const parsedStartDate = component.targetStartDate
    ? new Date(component.targetStartDate)
    : null;

  const componentUrl = `${window.location.origin}/programs/${component.programId}`;
  const description = component.participantGuidance;
  const title = component.name;
  const meetingUrl = component.survey?.formFields?.[0]?.link?.url;

  const generateCalendarData = (
    attendees: string[],
  ): Record<CalendarType, string> => {
    const baseDetails = [description, `Join Meeting:`, `${meetingUrl}`, ""];

    const googleDetails = [
      ...baseDetails,
      "🔗 Component Link:",
      componentUrl,
    ].join("\n");

    const outlookDetails = [
      description,
      "<br>",
      `Join Meeting:`,
      `<a href="${meetingUrl}">${meetingUrl}</a>`,
      "<br>",
      "🔗 Access the component at:",
      `<a href="${componentUrl}">${componentUrl}</a>`,
    ].join("<br>");

    return generateAllCalendarData({
      title: title || "",
      description: description || "",
      meetingUrl: meetingUrl || "",
      componentUrl,
      startDate: parsedStartDate,
      endDate: parsedEndDate,
      timezone: selectedTimezone,
      attendees,
      googleDetails,
      outlookDetails,
    });
  };

  const [preferredCalendar, setPreferredCalendar] = useCalendarPreference();
  const [clickHistory, setClickHistory] = useState(() => {
    const stored = localStorage.getItem("calendarClickHistory");
    return stored ? JSON.parse(stored) : {};
  });

  const trackCalendarClick = (calendarType: CalendarType) => {
    const newHistory = {
      ...clickHistory,
      [component.id]: {
        lastClicked: Date.now(),
        calendarType,
      },
    };
    setClickHistory(newHistory);
    localStorage.setItem("calendarClickHistory", JSON.stringify(newHistory));
  };

  const handleCalendarSelect = async (service: CalendarType) => {
    const owners = ensureNonNullArray(program.programOwners);
    let attendees = owners
      .map((owner) => owner.user?.email)
      .filter((e) => e !== undefined);

    try {
      const { data } = await fetch({
        variables: {
          programComponentId: component.id,
        },
      });

      const participants = ensureNonNullArray(
        data?.programComponentParticipants?.participants,
      )
        .map((p) => p.user?.email)
        .filter((e) => e !== undefined);

      attendees.push(...participants);

      setPreferredCalendar(service);
      trackCalendarClick(service);
      const calendarData = generateCalendarData(attendees);

      if (service === "apple") {
        const link = document.createElement("a");
        link.href = calendarData.ics;
        link.download = "event.ics";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(calendarData[service], "_blank");
      }
    } catch (error) {
      console.log("Error fetching calendar data", error);
    }
  };

  const wasClicked = !!clickHistory[component.id];

  return {
    handleCalendarSelect,
    clickHistory,
    preferredCalendar,
    wasClicked,
    loading,
  };
}

interface TimeContentProps {
  component: LeadComponent | ParticipantComponent | MainComponent;
  program: MainProgram;
}

function TimeContent({ component, program }: TimeContentProps) {
  const eventId = component.id;
  const { handleCalendarSelect, clickHistory, preferredCalendar, loading } =
    useCalendarClick(component, program);
  const calendarConfigs = [
    {
      id: "google",
      name: "Google Calendar",
      icon: GoogleCalendarIcon,
      onClick: () => handleCalendarSelect("google"),
    },
    {
      id: "outlook",
      name: "Outlook Calendar",
      icon: OutlookIcon,
      onClick: () => handleCalendarSelect("outlook"),
    },
    {
      id: "apple",
      name: "Apple Calendar / Other",
      icon: CalendarIcon,
      onClick: () => handleCalendarSelect("apple"),
    },
  ];

  const wasClicked = clickHistory[eventId];

  return (
    <Box p={16} width={350}>
      <Flex flexDirection="column" style={{ gap: 8 }}>
        {wasClicked && (
          <p
            style={{
              fontSize: 14,
              color: "#778397",
            }}
          >
            Last opened in {upperFirst(clickHistory[eventId].calendarType)}{" "}
            {formatDistanceToNow(new Date(clickHistory[eventId].lastClicked))}
          </p>
        )}
        {calendarConfigs.map((config) => (
          <>
            <CalendarButton
              loading={loading}
              key={config.name}
              icon={<EmbedSvg width={18} height={18} src={config.icon} />}
              onClick={config.onClick}
            >
              <Flex justifyContent="space-between" alignItems="center" flex={1}>
                {config.name}
                {preferredCalendar === config.id && (
                  <Box
                    css={{
                      fontSize: 12,
                      color: "#778397",
                      fontWeight: 400,
                    }}
                  >
                    Preferred
                  </Box>
                )}
              </Flex>
            </CalendarButton>
          </>
        ))}
      </Flex>
    </Box>
  );
}

interface MeetingScheduleButtonProps {
  component: LeadComponent | ParticipantComponent | MainComponent;
  program: MainProgram;
}

export function MeetingScheduleButton({
  component,
  program,
}: MeetingScheduleButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { handleCalendarSelect, preferredCalendar, wasClicked, loading } =
    useCalendarClick(component, program);

  const meetingUrl = component.survey?.formFields?.[0]?.link?.url;

  if (!isValidHttpOrHttpsUrl(meetingUrl)) {
    return <></>;
  }
  const handleMainButtonClick = () => {
    if (preferredCalendar) {
      handleCalendarSelect(preferredCalendar);
    } else {
      setIsOpen(true);
    }
  };

  const handleDropdownClick = () => {
    if (!preferredCalendar) {
      handleCalendarSelect(preferredCalendar);
    } else {
      setIsOpen(true);
    }
  };

  const buttons = (
    <div className="flex">
      <Button
        loading={loading}
        icon={
          !wasClicked ? (
            <EmbedSvg width={14} height={14} src={CalendarGrayIcon} />
          ) : (
            <EmbedSvg width={14} height={14} src={CalendarGrayCheckIcon} />
          )
        }
        className={CARD_CLASSES}
        style={{
          fontSize: 14,
          padding: "8px 20px",
          borderRadius: "4px 0 0 4px",
        }}
        onClick={handleMainButtonClick}
      >
        Add to Calendar
      </Button>
      <Button
        onClick={handleDropdownClick}
        className={CARD_CLASSES}
        style={{
          fontSize: 14,
          padding: "8px 8px",
          borderRadius: "0px 4px 4px 0px",
          borderLeft: "none",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
        }}
      >
        <Icon icon="caret-down" size={16} />
      </Button>
    </div>
  );

  return (
    <Box minWidth={200}>
      <Popover
        canEscapeKeyClose={true}
        position={Position.BOTTOM_RIGHT}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        content={<TimeContent component={component} program={program} />}
        className="rct-item-content"
      >
        {buttons}
      </Popover>
    </Box>
  );
}

const CalendarButton = styled(Button)`
  &.${Classes.BUTTON} {
    border: 1px solid #e2e2e2;
    box-shadow: none;
    justify-content: start;
    &:hover {
      box-shadow: none;
      background-color: ${Colors.WHITE};
      color: ${BRAND_PURPLE};
    }
    min-width: 300px;
    .${BP_NS}-button-text {
      flex: 1;
    }
  }

  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    background-color: ${Colors.WHITE};
    box-shadow: none;
    &:hover {
      box-shadow: none;
      background-color: ${Colors.WHITE};
      color: ${BRAND_PURPLE};
      border: 1px solid ${BRAND_PURPLE};
    }
    &.${BP_NS}-active {
      color: ${BRAND_PURPLE};
      border: 1px solid ${BRAND_PURPLE};
    }
  }
  :focus {
    outline: none;
    box-shadow: none;
    outline-offset: 0;
  }
`;
